// components/CreateCaseModal.jsx - FIXED VERSION
import React, { useState, useEffect, useRef } from "react";
import { X, Search, Loader, AlertCircle, AlertTriangle } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateCaseModal = ({ account, contact, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    accountId: account?.id || "",
    contactId: contact?.id || "",
    subject: "",
    description: "",
    internalComments: "",
    status: "New",
    priority: "Medium",
    type: "",
    origin: "",
    reason: "",
    webEmail: "",
    webName: "",
    webCompany: "",
    webPhone: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [accountSearchQuery, setAccountSearchQuery] = useState("");
  const [accountSearchResults, setAccountSearchResults] = useState([]);
  const [accountSearchLoading, setAccountSearchLoading] = useState(false);
  const [showAccountResults, setShowAccountResults] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(account || null);

  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [showContactResults, setShowContactResults] = useState(false);
  const [selectedContact, setSelectedContact] = useState(contact || null);

  const [accountContacts, setAccountContacts] = useState([]);
  const [accountContactsLoading, setAccountContactsLoading] = useState(false);

  const [openCases, setOpenCases] = useState([]);
  const [openCasesLoading, setOpenCasesLoading] = useState(false);

  const accountSearchRef = useRef(null);
  const contactSearchRef = useRef(null);

  const statusOptions = [
    "New",
    "In Progress",
    "On Hold",
    "Escalated",
    "Closed",
  ];
  const priorityOptions = ["Low", "Medium", "High"];
  const typeOptions = [
    { label: "--None--", value: "--None--" },
    { label: "Technical Fault Line 1", value: "Technical Fault Line 1" },
    { label: "Technical Fault Line 2", value: "Technical Fault Line 2" },
    { label: "Base Fault Queue", value: "Base Fault Queue" },
    { label: "Install Problems", value: "Install Problems" },
    { label: "Porting", value: "Porting" },
    { label: "Revisit Required", value: "Revisit Required" },
    { label: "Remote Install Queue", value: "Remote Install Queue" },
    { label: "Temp Cease of Services", value: "Temp Cease of Services" },
    { label: "Permanent Cease", value: "Permanent Cease" },
    { label: "Office Issue", value: "Office Issue" },
    { label: "Billing Query", value: "Billing Query" },
    { label: "Sales Issue", value: "Sales Issue" },
    { label: "Printer Issue", value: "Printer Issue" },
    { label: "Additional Equipment", value: "Additional Equipment" },
    { label: "CCTV Fault", value: "CCTV Fault" },
    { label: "ESCALATIONS", value: "ESCALATIONS" },
    { label: "Migrations", value: "migrations" },
  ];
  const originOptions = ["--None--", "Email", "Phone", "Web"];
  const reasonOptions = [
    "--None--",
    "User didn't attend training",
    "Complex functionality",
    "Existing problem",
    "Instructions not clear",
    "New problem",
  ];

  const isAccountBlocked =
    selectedAccount &&
    (selectedAccount.accountStatus === "inactive" ||
      selectedAccount.accountStatus === "dead" ||
      selectedAccount.accountStatus === "suspended");

  useEffect(() => {
    const fetchAccountContacts = async () => {
      if (!selectedAccount?.id) {
        setAccountContacts([]);
        return;
      }
      try {
        setAccountContactsLoading(true);
        const response = await apiService.getContacts({
          accountId: selectedAccount.id,
          limit: 100,
        });
        setAccountContacts(response.data || []);
      } catch (error) {
        console.error("Error fetching account contacts:", error);
        setAccountContacts([]);
      } finally {
        setAccountContactsLoading(false);
      }
    };
    fetchAccountContacts();
  }, [selectedAccount]);

  useEffect(() => {
    const fetchOpenCases = async () => {
      if (!selectedAccount?.id) {
        setOpenCases([]);
        return;
      }
      try {
        setOpenCasesLoading(true);
        const response = await apiService.getAccountOpenCases(
          selectedAccount.id,
        );
        setOpenCases(response.data || []);
      } catch (error) {
        console.error("Error fetching open cases:", error);
        setOpenCases([]);
      } finally {
        setOpenCasesLoading(false);
      }
    };
    fetchOpenCases();
  }, [selectedAccount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accountSearchRef.current &&
        !accountSearchRef.current.contains(event.target)
      )
        setShowAccountResults(false);
      if (
        contactSearchRef.current &&
        !contactSearchRef.current.contains(event.target)
      )
        setShowContactResults(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (accountSearchQuery.length < 2) {
      setAccountSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setAccountSearchLoading(true);
        const response = await apiService.getAccounts({
          search: accountSearchQuery,
          limit: 10,
        });
        setAccountSearchResults(response.data || []);
        setShowAccountResults(true);
      } catch (error) {
        console.error("Error searching accounts:", error);
      } finally {
        setAccountSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [accountSearchQuery]);

  useEffect(() => {
    if (contactSearchQuery.length < 2) {
      setContactSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setContactSearchLoading(true);
        const searchParams = { search: contactSearchQuery, limit: 10 };
        if (selectedAccount) searchParams.accountId = selectedAccount.id;
        const response = await apiService.getContacts(searchParams);
        setContactSearchResults(response.data || []);
        setShowContactResults(true);
      } catch (error) {
        console.error("Error searching contacts:", error);
      } finally {
        setContactSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearchQuery, selectedAccount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isAccountBlocked) {
      setError("Cannot create case for an inactive or dead account");
      return;
    }
    if (!selectedAccount) {
      setError("Account Name is required");
      return;
    }
    if (!formData.webPhone || formData.webPhone.trim() === "") {
      setError("Web Phone is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const sanitizeValue = (value) =>
        value === "" || value === "--None--" ? null : value;
      const submitData = {
        accountId: selectedAccount?.id || null,
        contactId: selectedContact?.id || null,
        subject: sanitizeValue(formData.subject),
        description: sanitizeValue(formData.description),
        internalComments: sanitizeValue(formData.internalComments),
        status: formData.status || "New",
        priority: formData.priority || "Medium",
        type: sanitizeValue(formData.type),
        origin: sanitizeValue(formData.origin),
        reason: sanitizeValue(formData.reason),
        webEmail: sanitizeValue(formData.webEmail),
        webName: sanitizeValue(formData.webName),
        webCompany: sanitizeValue(formData.webCompany),
        webPhone: sanitizeValue(formData.webPhone),
      };
      await apiService.createCase(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating case:", err);
      setError(err.response?.data?.message || "Failed to create case");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setAccountSearchQuery("");
    setShowAccountResults(false);
    if (selectedContact && selectedContact.accountId !== account.id) {
      setSelectedContact(null);
      setContactSearchQuery("");
    }
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setContactSearchQuery("");
    setShowContactResults(false);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "text-red-400";
      case "Medium":
        return "text-yellow-400";
      case "Low":
        return "text-green-400";
      default:
        return "text-gray-400";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return "bg-blue-500/20 text-blue-400";
      case "In Progress":
        return "bg-yellow-500/20 text-yellow-400";
      case "On Hold":
        return "bg-orange-500/20 text-orange-400";
      case "Escalated":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  // Theme helpers
  const inputClass = `w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDarkMode
      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `text-sm pt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`;

  const sectionBgClass = `px-4 py-2 mb-4 rounded ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`;

  const sectionHeadingClass = `text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;

  const dropdownClass = `absolute z-10 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-y-auto ${
    isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"
  }`;

  const dropdownItemClass = `w-full px-4 py-3 text-left transition-colors border-b last:border-b-0 ${
    isDarkMode
      ? "hover:bg-gray-800 border-gray-800 text-white"
      : "hover:bg-gray-50 border-gray-100 text-gray-900"
  }`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col ${
          isDarkMode ? "bg-[#13161B]" : "bg-white"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            New Case
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {isAccountBlocked && (
            <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/50 rounded flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-orange-400 font-medium capitalize">
                  Account is {selectedAccount.accountStatus}
                  {selectedAccount.accountStatusReason && (
                    <span className="font-normal">
                      {" "}
                      — {selectedAccount.accountStatusReason}
                    </span>
                  )}
                </p>
                <p className="text-xs text-orange-400/70 mt-1">
                  Cannot create a case for this account
                </p>
              </div>
            </div>
          )}

          <form id="caseForm" onSubmit={handleSubmit}>
            {/* Case Information Section */}
            <div className={sectionBgClass}>
              <h3 className={sectionHeadingClass}>Case Information</h3>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {/* Account Name */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>
                  Account Name <span className="text-red-500">*</span>
                </label>
                <div className="col-span-2 relative" ref={accountSearchRef}>
                  {selectedAccount ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex-1 px-3 py-2 border rounded flex items-center gap-2 ${
                            isAccountBlocked
                              ? "border-orange-500/50"
                              : isDarkMode
                                ? "border-gray-700"
                                : "border-gray-300"
                          } ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-white text-gray-900"}`}
                        >
                          <span>{selectedAccount.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAccount(null);
                            setOpenCases([]);
                            setAccountContacts([]);
                          }}
                          className={`px-3 py-2 text-sm transition-colors ${
                            isDarkMode
                              ? "text-gray-400 hover:text-white"
                              : "text-gray-500 hover:text-gray-900"
                          }`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input
                        type="text"
                        value={accountSearchQuery}
                        onChange={(e) => setAccountSearchQuery(e.target.value)}
                        placeholder="Search accounts..."
                        className={`${inputClass} pl-10`}
                      />
                      {accountSearchLoading && (
                        <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />
                      )}
                    </div>
                  )}

                  {showAccountResults && accountSearchResults.length > 0 && (
                    <div className={dropdownClass}>
                      {accountSearchResults.map((account) => {
                        const isBlocked =
                          account.accountStatus === "inactive" ||
                          account.accountStatus === "dead" ||
                          account.accountStatus === "suspended";
                        return (
                          <button
                            key={account.id}
                            type="button"
                            onClick={() => handleSelectAccount(account)}
                            className={dropdownItemClass}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {account.name}
                              </span>
                              {isBlocked && (
                                <span className="px-1.5 py-0.5 text-xs font-medium bg-orange-500/20 text-orange-400 rounded capitalize">
                                  {account.accountStatus}
                                </span>
                              )}
                            </div>
                            {account.type && (
                              <div
                                className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {account.type}
                              </div>
                            )}
                            {isBlocked && account.accountStatusReason && (
                              <div className="text-xs text-orange-400/60 mt-0.5">
                                {account.accountStatusReason}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Name */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Contact Name</label>
                <div className="col-span-2 relative" ref={contactSearchRef}>
                  {selectedContact ? (
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex-1 px-3 py-2 border rounded ${
                          isDarkMode
                            ? "border-gray-700 bg-[#0C0E12] text-white"
                            : "border-gray-300 bg-white text-gray-900"
                        }`}
                      >
                        {selectedContact.firstName} {selectedContact.lastName}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedContact(null)}
                        className={`px-3 py-2 text-sm transition-colors ${
                          isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                        }`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input
                        type="text"
                        value={contactSearchQuery}
                        onChange={(e) => setContactSearchQuery(e.target.value)}
                        onFocus={() => {
                          if (selectedAccount && accountContacts.length > 0)
                            setShowContactResults(true);
                        }}
                        placeholder={
                          selectedAccount
                            ? "Search or select from account contacts..."
                            : "Select an account first..."
                        }
                        className={`${inputClass} pl-10`}
                        disabled={!selectedAccount}
                      />
                      {(contactSearchLoading || accountContactsLoading) && (
                        <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />
                      )}
                    </div>
                  )}

                  {showContactResults && selectedAccount && (
                    <div className={dropdownClass}>
                      {contactSearchQuery.length >= 2
                        ? contactSearchResults.map((contact) => (
                            <button
                              key={contact.id}
                              type="button"
                              onClick={() => handleSelectContact(contact)}
                              className={dropdownItemClass}
                            >
                              <div
                                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {contact.firstName} {contact.lastName}
                              </div>
                              {contact.email && (
                                <div
                                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  {contact.email}
                                </div>
                              )}
                              {contact.title && (
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  {contact.title}
                                </div>
                              )}
                            </button>
                          ))
                        : accountContacts.map((contact) => (
                            <button
                              key={contact.id}
                              type="button"
                              onClick={() => handleSelectContact(contact)}
                              className={dropdownItemClass}
                            >
                              <div
                                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {contact.firstName} {contact.lastName}
                              </div>
                              {contact.email && (
                                <div
                                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  {contact.email}
                                </div>
                              )}
                              {contact.title && (
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  {contact.title}
                                </div>
                              )}
                            </button>
                          ))}
                      {contactSearchQuery.length < 2 &&
                        accountContacts.length === 0 && (
                          <div
                            className={`px-4 py-3 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            No contacts found for this account
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Status</label>
                <div className="col-span-2">
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    className={inputClass}
                  >
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Type */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>
                  Type<span className="text-red-500">*</span>
                </label>
                <div className="col-span-2">
                  <select
                    value={formData.type}
                    onChange={(e) => handleChange("type", e.target.value)}
                    required
                    className={inputClass}
                  >
                    {typeOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Case Origin */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Case Origin</label>
                <div className="col-span-2">
                  <select
                    value={formData.origin}
                    onChange={(e) => handleChange("origin", e.target.value)}
                    className={inputClass}
                  >
                    {originOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Case Reason */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Case Reason</label>
                <div className="col-span-2">
                  <select
                    value={formData.reason}
                    onChange={(e) => handleChange("reason", e.target.value)}
                    className={inputClass}
                  >
                    {reasonOptions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Priority</label>
                <div className="col-span-2">
                  <select
                    value={formData.priority}
                    onChange={(e) => handleChange("priority", e.target.value)}
                    className={inputClass}
                  >
                    {priorityOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Open Cases Section */}
            {selectedAccount && (
              <>
                <div
                  className={`${sectionBgClass} mt-6 flex items-center justify-between`}
                >
                  <h3 className={sectionHeadingClass}>
                    Open Cases for {selectedAccount.name}
                  </h3>
                  {openCasesLoading && (
                    <Loader className="w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>

                {!openCasesLoading && openCases.length === 0 && (
                  <div
                    className={`text-sm mb-4 px-4 py-3 rounded ${
                      isDarkMode
                        ? "text-gray-400 bg-gray-800/30"
                        : "text-gray-500 bg-gray-50"
                    }`}
                  >
                    No open cases found for this account
                  </div>
                )}

                {!openCasesLoading && openCases.length > 0 && (
                  <div className="mb-4 space-y-2 max-h-48 overflow-y-auto">
                    {openCases.map((caseItem) => (
                      <div
                        key={caseItem.id}
                        className={`p-3 rounded border transition-colors ${
                          isDarkMode
                            ? "bg-gray-800/30 border-gray-700/50 hover:border-gray-600"
                            : "bg-gray-50 border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                #{caseItem.caseNumber}
                              </span>
                              <span
                                className={`px-2 py-0.5 text-xs rounded ${getStatusColor(caseItem.status)}`}
                              >
                                {caseItem.status}
                              </span>
                              <span
                                className={`text-xs ${getPriorityColor(caseItem.priority)}`}
                              >
                                {caseItem.priority}
                              </span>
                            </div>
                            <p
                              className={`text-sm truncate ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              {caseItem.subject || "No subject"}
                            </p>
                            {caseItem.type && (
                              <p
                                className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                              >
                                {caseItem.type}
                              </p>
                            )}
                          </div>
                          <div
                            className={`text-xs whitespace-nowrap ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {new Date(caseItem.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Description Information Section */}
            <div className={`${sectionBgClass} mt-6`}>
              <h3 className={sectionHeadingClass}>Description Information</h3>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Subject</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleChange("subject", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Description</label>
                <div className="col-span-2">
                  <textarea
                    rows="4"
                    value={formData.description}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Internal Comments</label>
                <div className="col-span-2">
                  <textarea
                    rows="3"
                    value={formData.internalComments}
                    onChange={(e) =>
                      handleChange("internalComments", e.target.value)
                    }
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>
            </div>

            {/* Web Information Section */}
            <div className={`${sectionBgClass} mt-6`}>
              <h3 className={sectionHeadingClass}>Web Information</h3>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Web Email</label>
                <div className="col-span-2">
                  <input
                    type="email"
                    value={formData.webEmail}
                    onChange={(e) => handleChange("webEmail", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Web Company</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.webCompany}
                    onChange={(e) => handleChange("webCompany", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Web Name</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.webName}
                    onChange={(e) => handleChange("webName", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>
                  Web Phone <span className="text-red-500">*</span>
                </label>
                <div className="col-span-2">
                  <input
                    type="tel"
                    value={formData.webPhone}
                    onChange={(e) => handleChange("webPhone", e.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-100"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="caseForm"
            disabled={loading || isAccountBlocked}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title={
              isAccountBlocked
                ? "Cannot create case for inactive, suspended or dead account"
                : ""
            }
          >
            {loading && <Loader className="w-4 h-4 animate-spin" />}
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCaseModal;
